(() => {
  const initializeStudio = (studio) => {
    if (studio.dataset.lytexaStudioInitialized === 'true') {
      return;
    }

    studio.dataset.lytexaStudioInitialized = 'true';

    const viewer = studio.querySelector(
      '[data-lytexa-pattern-layer]'
    );

    const mockupLayer = studio.querySelector(
      '[data-lytexa-mockup-layer]'
    );

    const scaleInput = studio.querySelector(
      '[data-lytexa-pattern-scale]'
    );

    const colorwayButtons = Array.from(
      studio.querySelectorAll('[data-lytexa-colorway]')
    );

    const patternButton = studio.querySelector(
      '[data-lytexa-pattern-preview]'
    );

    const mockupButtons = Array.from(
      studio.querySelectorAll('[data-lytexa-mockup]')
    );

    const previewButtons = patternButton
      ? [patternButton, ...mockupButtons]
      : mockupButtons;

    let patternOffsetX = 0;
    let patternOffsetY = 0;
    let dragStartX = 0;
    let dragStartY = 0;
    let dragOriginX = 0;
    let dragOriginY = 0;
    let activePointerId = null;
    let isDragging = false;

    const setActiveButton = (buttons, activeButton) => {
      buttons.forEach((button) => {
        const isActive = button === activeButton;

        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', String(isActive));
      });
    };

    const updatePatternPosition = () => {
      if (!viewer) return;

      viewer.style.backgroundPosition =
        `calc(50% + ${patternOffsetX}px) ` +
        `calc(50% + ${patternOffsetY}px)`;
    };

    const resetPatternPosition = () => {
      patternOffsetX = 0;
      patternOffsetY = 0;
      updatePatternPosition();
    };

    const updatePatternSize = () => {
      if (!viewer || !scaleInput) return;

      const scale = Number(scaleInput.value) || 100;

      viewer.style.backgroundSize = `${scale}% auto`;

      if (scale === 100) {
        scaleInput.setAttribute(
          'aria-valuetext',
          'Original size'
        );
      } else if (scale < 100) {
        scaleInput.setAttribute(
          'aria-valuetext',
          `${scale}% reduced`
        );
      } else {
        scaleInput.setAttribute(
          'aria-valuetext',
          `${scale}% enlarged`
        );
      }
    };

    const applyPatternImage = (imageUrl) => {
      if (!viewer || !imageUrl) return;

      viewer.style.backgroundImage = `url("${imageUrl}")`;
      viewer.style.backgroundRepeat = 'repeat';

      resetPatternPosition();
      updatePatternSize();
    };

    const selectColorway = (button) => {
      if (!button) return;

      const imageUrl = button.dataset.imageUrl;

      if (!imageUrl) return;

      applyPatternImage(imageUrl);
      setActiveButton(colorwayButtons, button);
    };

    const showPattern = () => {
      if (mockupLayer) {
        mockupLayer.hidden = true;
        mockupLayer.removeAttribute('src');
        mockupLayer.alt = '';
      }

      if (patternButton) {
        setActiveButton(previewButtons, patternButton);
      }
    };

    const selectMockup = (button) => {
      if (!mockupLayer || !button) return;

      const overlayUrl = button.dataset.overlayUrl;

      if (!overlayUrl) return;

      mockupLayer.src = overlayUrl;
      mockupLayer.alt =
        button.getAttribute('aria-label') || 'Mockup';
      mockupLayer.hidden = false;

      setActiveButton(previewButtons, button);
    };

    const startPatternDrag = (event) => {
      if (!viewer || event.button > 0) return;

      isDragging = true;
      activePointerId = event.pointerId;
      dragStartX = event.clientX;
      dragStartY = event.clientY;
      dragOriginX = patternOffsetX;
      dragOriginY = patternOffsetY;

      viewer.setPointerCapture?.(event.pointerId);
      viewer.style.cursor = 'grabbing';

      event.preventDefault();
    };

    const movePattern = (event) => {
      if (
        !isDragging ||
        event.pointerId !== activePointerId
      ) {
        return;
      }

      patternOffsetX =
        dragOriginX + event.clientX - dragStartX;

      patternOffsetY =
        dragOriginY + event.clientY - dragStartY;

      updatePatternPosition();
      event.preventDefault();
    };

    const stopPatternDrag = (event) => {
      if (
        !isDragging ||
        event.pointerId !== activePointerId
      ) {
        return;
      }

      isDragging = false;

      if (
        viewer.hasPointerCapture?.(event.pointerId)
      ) {
        viewer.releasePointerCapture(event.pointerId);
      }

      activePointerId = null;
      viewer.style.cursor = 'grab';
    };

    if (viewer) {
      viewer.style.cursor = 'grab';
      viewer.style.touchAction = 'none';
      viewer.style.userSelect = 'none';

      if (mockupLayer) {
        mockupLayer.style.pointerEvents = 'none';
        mockupLayer.style.userSelect = 'none';
        mockupLayer.draggable = false;
      }

      viewer.addEventListener(
        'pointerdown',
        startPatternDrag
      );

      viewer.addEventListener(
        'pointermove',
        movePattern
      );

      viewer.addEventListener(
        'pointerup',
        stopPatternDrag
      );

      viewer.addEventListener(
        'pointercancel',
        stopPatternDrag
      );

      viewer.addEventListener(
        'lostpointercapture',
        () => {
          isDragging = false;
          activePointerId = null;
          viewer.style.cursor = 'grab';
        }
      );
    }

    scaleInput?.addEventListener(
      'input',
      updatePatternSize
    );

    colorwayButtons.forEach((button) => {
      button.addEventListener('click', () => {
        selectColorway(button);
      });
    });

    patternButton?.addEventListener(
      'click',
      showPattern
    );

    mockupButtons.forEach((button) => {
      button.addEventListener('click', () => {
        selectMockup(button);
      });
    });

    const initialColorway =
      colorwayButtons.find((button) =>
        button.classList.contains('is-active')
      ) || colorwayButtons[0];

    if (initialColorway) {
      selectColorway(initialColorway);
    } else {
      applyPatternImage(
        viewer?.dataset.initialImageUrl || ''
      );
    }

    showPattern();
  };

  const initializeAllStudios = () => {
    document
      .querySelectorAll('[data-lytexa-product-studio]')
      .forEach(initializeStudio);
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initializeAllStudios
    );
  } else {
    initializeAllStudios();
  }

  document.addEventListener(
    'shopify:section:load',
    initializeAllStudios
  );
})();
